// NewMaterialPage
import React, { useEffect, useState } from 'react';
import {
  fetchNewMaterialMeasurements,
  fetchNewMaterialCategories,
  fetchQuantityTypes,
  addTestData,
  fetchTestData,
  fetchRegions,
  API_BASE_URL,
  fetchAthenaOc21Elements,
  saveAthenaQuOc21,
} from '../services/api';
import './PageLayout.css';
import { logAction } from '../services/api';
import { auth } from '../firebaseConfig';
import { v4 as uuidv4 } from 'uuid';
import { useRegion } from '../context/RegionContext';
import StyledDropdown from '../components/StyledDropdown';
import CategoryPickerModal from '../components/CategoryPickerModal';

const SuccessModal = ({ isOpen, onClose, title, message, icon }) => {
  if (!isOpen) return null;

  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header">
          <div className="success-modal-icon">
            {icon || "✅"}
          </div>
          <h2>{title || "Success!"}</h2>
        </div>
        <div className="success-modal-body">
          <p>{message}</p>
        </div>
        <div className="success-modal-footer">
          <button className="success-modal-button" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

function NewMaterialPage() {
  const [data, setData] = useState([]);
  const [NewMaterialMeasurements, setNewMaterialMeasurements] = useState([]);
  const [quantityTypes, setQuantityTypes] = useState([]);
  const [NewMaterialCategories, setNewMaterialCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  // ✅ Add regions state
  const [availableRegions, setAvailableRegions] = useState([]);

  const { selectedRegion } = useRegion();

  const [form, setForm] = useState({
    Id: uuidv4(),
    name: '',
    description: '',
    ColorHex: '#000000',
    quantityType: '',
    categoryId: '',
    materialKind: 'Assembly',   // 'Assembly' build-up | 'Product' single-EPD (Nezo EPD)
    visibility: 'public',
    ownerEmail: '',
    fromCompositeRequestId: '',
    oc21ElementId: '',          // Athena OC21 element (USA-Canada only; additive to categoryId)
  });

  // Athena OC21 element tree (USA-Canada only). Loaded lazily for vquest-us-1.
  const [oc21Elements, setOc21Elements] = useState([]);
  const isAthenaRegion = selectedRegion === 'vquest-us-1';

  // Raw quantity type from the prefill (may be an id OR a name like "CubicMeters").
  // Held until quantityTypes load so we can resolve it to a valid <select> option id.
  const [pendingQtyType, setPendingQtyType] = useState(null);

  // Autofill from Composite Requests (if present)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('newCompositePrefill');
      if (!raw) return;
      const p = JSON.parse(raw);
      const rawQtyType = p.quantityType || p.QuantityType || '';
      setForm((prev) => ({
        ...prev,
        name: p.name || p.Name || prev.name,
        description: p.description || p.Description || prev.description,
        ColorHex: p.ColorHex || prev.ColorHex,
        quantityType: rawQtyType || prev.quantityType,
        categoryId: p.categoryId || p.CategoryId || prev.categoryId,
        materialKind: p.materialKind || p.MaterialKind || prev.materialKind,
        visibility: p.Visibility || 'public',
        ownerEmail: p.OwnerEmail || '',
        fromCompositeRequestId: p._fromCompositeRequestId || '',
      }));
      // Resolve it to a real option id once quantityTypes are loaded (see effect below).
      if (rawQtyType) setPendingQtyType(rawQtyType);
      localStorage.removeItem('newCompositePrefill');
    } catch {}
  }, []);

  // Resolve the prefilled quantity type to a valid <select> option id.
  // The request may store it as an id (matches directly) or as a name
  // (e.g. "CubicMeters") that must be mapped to the option's id.
  useEffect(() => {
    if (!pendingQtyType || !quantityTypes.length) return;
    const rawStr = String(pendingQtyType).trim();
    let resolved = null;
    if (quantityTypes.some((t) => String(t.id) === rawStr)) {
      resolved = rawStr;
    } else {
      const byName = quantityTypes.find(
        (t) => String(t.name).trim().toLowerCase() === rawStr.toLowerCase()
      );
      if (byName) resolved = String(byName.id);
    }
    if (resolved) {
      setForm((prev) => ({ ...prev, quantityType: resolved }));
    } else {
      console.warn('[NewMaterial] Could not resolve prefilled quantity type:', pendingQtyType);
    }
    setPendingQtyType(null);
  }, [pendingQtyType, quantityTypes]);


  // ✅ Load regions on component mount
  useEffect(() => {
    loadRegions();
  }, []);

  // Load the Athena OC21 element tree — USA-Canada only (never runs for other regions).
  useEffect(() => {
    if (!isAthenaRegion) {
      setOc21Elements([]);
      return;
    }
    (async () => {
      try {
        const rows = await fetchAthenaOc21Elements();
        // CategoryPickerModal expects { id, name, ParentCategoryId, Priority }.
        setOc21Elements(
          (rows || []).map((e) => ({
            id: e.Id,
            name: e.full_label,
            ParentCategoryId: e.parent_id,
            Priority: e.code,
          }))
        );
      } catch (err) {
        console.error('❌ Error fetching OC21 elements:', err);
        setOc21Elements([]);
      }
    })();
  }, [isAthenaRegion]);

  // ✅ Reload data when region changes
  useEffect(() => {
    if (selectedRegion) {
      loadAllData();
    }
  }, [selectedRegion]);

  // ✅ Function to load regions
  const loadRegions = async () => {
    try {
      const regions = await fetchRegions();
      setAvailableRegions(regions);
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  };

  // ✅ Function to get region name by ID
  const getRegionName = (regionId) => {
    const region = availableRegions.find(r => r.id === regionId);
    return region ? region.name : regionId;
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadTestData(),
        loadNewMaterialMeasurements(),
        loadQuantityTypes(),
        loadNewMaterialCategories()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTestData = async () => {
    try {
      const materials = await fetchTestData(); // Now returns array directly
      console.log('✅ Loaded quantity units:', materials.length);
      console.log('🔍 First material:', materials[0]);
      setData(Array.isArray(materials) ? materials : []);
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      setData([]);
    }
  };

  const loadNewMaterialMeasurements = async () => {
    try {
      const measurements = await fetchNewMaterialMeasurements(); // Returns array directly
      console.log('✅ Loaded measurements:', measurements.length);
      setNewMaterialMeasurements(Array.isArray(measurements) ? measurements : []);
    } catch (error) {
      console.error('❌ Error fetching measurements:', error);
      setNewMaterialMeasurements([]);
    }
  };

  const loadQuantityTypes = async () => {
    try {
      const types = await fetchQuantityTypes(); // Returns array directly
      console.log('✅ Loaded quantity types:', types.length);
      setQuantityTypes(Array.isArray(types) ? types : []);
    } catch (error) {
      console.error('❌ Error fetching quantity types:', error);
      setQuantityTypes([]);
    }
  };

  const loadNewMaterialCategories = async () => {
    try {
      const categories = await fetchNewMaterialCategories(); // Returns array directly
      console.log('✅ Loaded categories:', categories.length);
      setNewMaterialCategories(Array.isArray(categories) ? categories : []);
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      setNewMaterialCategories([]);
    }
  };

  const getQuantityTypeName = (quantityTypeId) => {
    const quantityType = quantityTypes.find(type => type.id == quantityTypeId);
    return quantityType ? quantityType.name : quantityTypeId;
  };



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const generateGUID = () => {
    setForm({ ...form, Id: uuidv4() });
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    await addTestData(form);

    // Athena OC21 mapping — USA-Canada assemblies only, and only when an element was chosen.
    // Separate additive write; a failure here doesn't undo the (already-created) assembly.
    if (isAthenaRegion && form.materialKind === 'Assembly' && form.oc21ElementId) {
      try {
        await saveAthenaQuOc21({ quantityUnitId: form.Id, elementId: form.oc21ElementId });
      } catch (err) {
        console.error('⚠️ Assembly saved but OC21 mapping failed:', err);
      }
    }

    if (form.fromCompositeRequestId) {
      const token = await auth.currentUser?.getIdToken();

      await fetch(`${API_BASE_URL}/composite-requests/${form.fromCompositeRequestId}/link-quantity-unit`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-user-role': 'admin',
          'x-user-id': auth.currentUser?.uid || 'unknown',
          'x-user-email': auth.currentUser?.email || 'unknown',
          'x-region': selectedRegion,
        },
        body: JSON.stringify({ quantityUnitId: form.Id }),
      });
    }
    
    setForm({
      Id: uuidv4(),
      name: '',
      description: '',
      ColorHex: '#000000',
      quantityType: '',
      categoryId: '',
      materialKind: 'Assembly',
      oc21ElementId: '',
    });

    await loadTestData();
    
    // Replace alert with custom modal
    setShowSuccessModal(true);
    
    logAction(
      auth.currentUser?.uid || 'unknown',
      auth.currentUser?.email || 'unknown',
      'ADD_NEW_MATERIAL',
      `Added material: ${form.name} to region: ${selectedRegion}`
    );
  } catch (error) {
    console.error('Error saving data:', error);
    alert('Error adding material. Please try again.');
  }
};

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchQuery)
    )
  );

  if (loading) {
    return (
      <div className="rate-builder-page">
        <h1>New Material</h1>
        <div className="region-indicator-under-title">
          🌏 Current Region: <strong>{getRegionName(selectedRegion)}</strong>
        </div>
        <div>Loading materials for {getRegionName(selectedRegion)}...</div>
      </div>
    );
  }

  const qtyTypeOptions = quantityTypes.map(t => ({ value: t.id, label: t.name }));

  // Products file under MaterialType (Nezo EPD) categories; assemblies under BuildingElement
  // categories. Hidden (retired shadow) categories never appear. If the chosen kind has no
  // matching categories yet (e.g. before the Nezo EPD tree is seeded), fall back to all
  // visible categories so authoring is never blocked.
  const visibleCategories = NewMaterialCategories.filter(c => !c.IsHidden);
  const wantedKind = form.materialKind === 'Product' ? 'MaterialType' : 'BuildingElement';
  const kindMatchedCategories = visibleCategories.filter(
    c => (c.CategoryKind || 'BuildingElement') === wantedKind
  );
  const categorySource = kindMatchedCategories.length ? kindMatchedCategories : visibleCategories;


  return (
    <div className="rate-builder-page">
      {/* ✅ Updated layout with region indicator under title */}
      <div className="page-title-section">
        <h1>New Material</h1>
        <div className="region-indicator-under-title">
          🌏 Current Region: <strong>{getRegionName(selectedRegion)}</strong>
        </div>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label htmlFor="Id">GUID</label>
            <div className="nm-guid-row">
              <input type="text" name="Id" value={form.Id} readOnly />
              <button type="button" className="calculate-button" onClick={generateGUID}>
                Generate
              </button>
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleInputChange}
              placeholder="Name"
              required
            />
          </div>

          <div className="input-group">
            <label htmlFor="description">Description</label>
            <input
              type="text"
              name="description"
              value={form.description}
              onChange={handleInputChange}
              placeholder="Description"
              required
            />
          </div>

          <div className="input-group">
            <label htmlFor="ColorHex">Color Hex</label>
            <input
              type="color"
              name="ColorHex"
              value={form.ColorHex}
              onChange={handleInputChange}
            />
            <input
              type="text"
              name="ColorHexText"
              value={form.ColorHex}
              readOnly
            />
          </div>

          <div className="input-group">
            <label htmlFor="quantityType">Quantity Type</label>
            <div style={{ marginTop: '15px', width: '55%' }}>
              <StyledDropdown
                name="quantityType"
                options={qtyTypeOptions}
                value={form.quantityType}
                onChange={(val) =>
                  setForm((prev) => ({ ...prev, quantityType: val }))
                }
                placeholder="Select a Quantity Type"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Material Kind</label>
            <div className="mk-options" role="radiogroup" aria-label="Material Kind">
              <button
                type="button"
                role="radio"
                aria-checked={form.materialKind === 'Assembly'}
                className={`mk-card ${form.materialKind === 'Assembly' ? 'mk-card--active' : ''}`}
                onClick={() => setForm((prev) => ({ ...prev, materialKind: 'Assembly', categoryId: '' }))}
              >
                <span className="mk-card__icon">🏗️</span>
                <span className="mk-card__text">
                  <span className="mk-card__title">Assembly</span>
                  <span className="mk-card__desc">Build-up, filed by building element</span>
                </span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={form.materialKind === 'Product'}
                className={`mk-card ${form.materialKind === 'Product' ? 'mk-card--active' : ''}`}
                onClick={() => setForm((prev) => ({ ...prev, materialKind: 'Product', categoryId: '' }))}
              >
                <span className="mk-card__icon">🌿</span>
                <span className="mk-card__text">
                  <span className="mk-card__title">Product / Nezo EPD</span>
                  <span className="mk-card__desc">Single EPD, building element chosen at mapping</span>
                </span>
              </button>
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="categoryId">
              {form.materialKind === 'Product' ? 'Material Type (Nezo EPD)' : 'Building Element Category'}
            </label>
            <div style={{ width: '55%' }}>
              <CategoryPickerModal
                categories={categorySource}
                value={form.categoryId}
                onChange={(val) => setForm((prev) => ({ ...prev, categoryId: val }))}
                placeholder={form.materialKind === 'Product' ? 'Select a material type' : 'Select a building element'}
                title={form.materialKind === 'Product' ? 'Select Material Type (Nezo EPD)' : 'Select Building Element Category'}
              />
            </div>
          </div>

          {/* Athena OC21 — Element (UniFormat): USA-Canada assemblies only. Additive to the
              Nezo Building Element Category above; drives the Athena BoM export's OC21 column.
              CategoryPickerModal parent rows have a "Select" button, so a top-level group OR a
              drilled-down sub-element can be chosen — whatever node is picked is written through. */}
          {isAthenaRegion && form.materialKind === 'Assembly' && (
            <div className="input-group">
              <label htmlFor="oc21ElementId">
                OC 21 — Element (UniFormat){' '}
                <span style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: '#2f7d72',
                  background: '#eef5f3', padding: '2px 6px', borderRadius: 4, marginLeft: 6,
                }}>USA-CANADA</span>
              </label>
              <div style={{ width: '55%' }}>
                <CategoryPickerModal
                  categories={oc21Elements}
                  value={form.oc21ElementId}
                  onChange={(val) => setForm((prev) => ({ ...prev, oc21ElementId: val }))}
                  placeholder="Select an OmniClass element"
                  title="Select OC 21 — Element (UniFormat)"
                />
              </div>
            </div>
          )}
          {/* Visibility indicator - shown when prefilled from a composite request */}
          {form.ownerEmail && (
            <div className="input-group">
              <label>Visibility</label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                borderRadius: 8,
                background: form.visibility === 'private' ? '#fef3c7' : '#dbeafe',
                border: `1px solid ${form.visibility === 'private' ? '#f59e0b' : '#93c5fd'}`,
                fontWeight: 'bold',
                color: form.visibility === 'private' ? '#92400e' : '#1e40af',
              }}>
                {form.visibility === 'private' ? '🔒 Private' : '🌐 Public'}
                <span style={{ fontWeight: 'normal', fontSize: 13 }}>
                  {form.visibility === 'private'
                    ? ` — only visible to ${form.ownerEmail}`
                    : ' — visible to all users in this region'}
                </span>
              </div>
            </div>
          )}    
          <button type="submit">Add Composite</button>
        </form>
      </div>

      {/* Add this before the closing </div> */}
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          title="Material Added Successfully!"
          message={`Material has been added to region ${getRegionName(selectedRegion)}.`}
          icon="🎉"
        />
    </div>
  );
}

export default NewMaterialPage;
